import { useEffect, useState } from 'react';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, TextAreaField } from '../../components/ui/Field.js';
import { useMaintenanceState, useUpdateMaintenance } from './api.js';

const DEFAULT_MESSAGE =
  'Catalyst PSA is undergoing maintenance. Changes are temporarily disabled — please check back shortly.';

export function MaintenancePage(): JSX.Element {
  const { isAdmin } = useAuth();
  const stateQuery = useMaintenanceState(isAdmin);
  const updateMutation = useUpdateMaintenance();
  const state = stateQuery.data;

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!state) return;
    setEnabled(state.enabled);
    setMessage(state.message ?? '');
  }, [state]);

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Maintenance mode" />
        <Card>
          <p className="text-sm text-slate-600">
            You need administrator privileges to manage maintenance mode.
          </p>
        </Card>
      </div>
    );
  }

  const handleSave = (): void => {
    setSaved(false);
    updateMutation.mutate(
      { enabled, message: message.trim() || null },
      { onSuccess: () => setSaved(true) },
    );
  };

  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save maintenance settings.'
        : null;

  return (
    <div>
      <PageHeader
        title="Maintenance mode"
        description="Temporarily block changes during a deploy so no user write lands mid-update."
      />

      <Card>
        <DataState
          isLoading={stateQuery.isLoading}
          isError={stateQuery.isError}
          error={stateQuery.error}
          onRetry={() => void stateQuery.refetch()}
          isEmpty={false}
        >
          {state ? (
            <div className="max-w-xl space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={state.enabled ? 'amber' : 'green'}>
                  {state.enabled ? 'Maintenance ON' : 'Live'}
                </Badge>
                {state.updatedAt ? (
                  <span className="text-xs text-slate-500">
                    Last changed {new Date(state.updatedAt).toLocaleString()}
                  </span>
                ) : null}
              </div>

              <p className="text-sm text-slate-500">
                While maintenance mode is on, non-admin users see a full-screen notice and the API
                rejects their changes (reads still work). Administrators can keep operating and turn
                it back off. Turn it on just before a risky deploy, then off once it completes.
              </p>

              <CheckboxField
                label="Enable maintenance mode"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />

              <TextAreaField
                label="Message shown to users"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={DEFAULT_MESSAGE}
              />

              {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
              {saved ? (
                <p className="text-sm text-emerald-600">Maintenance settings saved.</p>
              ) : null}

              <div className="flex items-center gap-2">
                <Button onClick={handleSave} isLoading={updateMutation.isPending}>
                  Save
                </Button>
              </div>
            </div>
          ) : null}
        </DataState>
      </Card>
    </div>
  );
}
